import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output
} from "@angular/core";
import {Subscription} from "rxjs";
import {SessionModel} from "../../../orchestration/session.model";
import {SessionEvents} from "../../../orchestration/session-events";
import {ColorPairData} from "../../../orchestration/app-entity.model";
import {ThemeModel} from "../../../theme/theme.model";
import {Color} from "../../../ui-elements/color";
import {MessageLevel} from "../../../notification/message/message";
import {ResponsiveService} from "../../../orchestration/responsive.service";
import {TyroPayRequest, TyroPayRequestError, TyroPayRequestErrorType, TyroPayRequestStatus} from "../../tyro";
import {PaymentModel} from "../../payment.model";
import {PaymentEvents} from "../../payment-events";
import {
    CardPaymentSubmissionFailure,
    CardPaymentSubmissionResult,
    PaymentAuthorisationType,
    PaymentCard
} from "../../payment";

@Component({
    selector: "woa-tyro-manual-payment-card-panel",
    templateUrl: "./tyro-manual-payment-card-panel.component.html",
    providers: [{provide: PaymentCard, useExisting: TyroManualPaymentCardPanelComponent}]
})
export class TyroManualPaymentCardPanelComponent extends PaymentCard implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() @HostBinding("class.disabled") disabled: boolean = false;

    @Output() onPayFormReady: EventEmitter<boolean> = new EventEmitter<boolean>();

    MessageLevel = MessageLevel;

    savePaymentDetails: boolean = false;
    payFormError: string;

    private _hidden: boolean = false;
    private hasBeenUnhidden: boolean = false;
    private payFormReady: boolean = false;
    private tyro: any;
    private payForm: any;
    private paymentPlatformPublicKey: string;
    private submitted: boolean = false;
    private subscriptions: Subscription = new Subscription();
    private destroyed: boolean = false;
    private tyroPayFormHolder: Element;

    constructor(private element: ElementRef,
                private sessionModel: SessionModel,
                private changeDetectorRef: ChangeDetectorRef,
                private responsiveService: ResponsiveService,
                public paymentModel: PaymentModel,
                private themeModel: ThemeModel) {
        super();
        this.savePaymentDetails = this.responsiveService.isMobile || this.sessionModel.devOrDemoMode;
    }

    @Input()
    set hidden(hidden: boolean) {
        this._hidden = hidden;
        if (!hidden) {
            if (!this.hasBeenUnhidden) {
                setTimeout(() => this.setupTyroPayForm());
            }
            this.hasBeenUnhidden = true;
        }
    }

    get hidden(): boolean {
        return this._hidden;
    }

    get showSpinner(): boolean {
        return !this.hidden && (!this.paymentModel.paymentPlatformPublicKey || (!this.payFormReady && !this.payFormError));
    }

    get showSavePaymentDetailsOption(): boolean {
        return !this.sessionModel.waiterMode;
    }

    ngOnInit(): void {
        PaymentEvents.paymentDetailsApplied$.subscribe(async () => {
            this.changeDetectorRef.detectChanges();
            return this.setupTyroPayForm();
        });
        this.subscriptions.add(SessionEvents.currentApp$.subscribe(() => this.changeDetectorRef.detectChanges()));
        this.subscriptions.add(this.responsiveService.windowWidth$.subscribe(async () => {
            if (this.payForm && this.payForm["resize"]) {
                this.payForm.resize();
            }
        }));
    }

    ngAfterViewInit(): void {
        // noinspection JSIgnoredPromiseFromCall
        this.setupTyroPayForm();
    }

    ngOnChanges(): void {
        // noinspection JSIgnoredPromiseFromCall
        this.setupTyroPayForm();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.subscriptions.unsubscribe();
    }

    canSavePaymentDetails(): boolean {
        return this.showSavePaymentDetailsOption && this.savePaymentDetails;
    }

    isValid(): boolean {
        this.submitted = true;
        return true;
    }

    markAsUnsubmitted(): void {
        this.submitted = false;
    }

    async submit(): Promise<CardPaymentSubmissionResult | CardPaymentSubmissionFailure> {
        try {
            await this.tyro.submitPay();
            const payRequest: TyroPayRequest = await this.tyro.fetchPayRequest();
            if (payRequest.status !== TyroPayRequestStatus.SUCCESS) {
                throw "Unexpected pay request status: " + payRequest.status;
            }
            return new CardPaymentSubmissionResult(PaymentAuthorisationType.TYRO_PAYMENT_INTENT);
        } catch (error) {
            console.error(error);
            if (error.type) {
                const payRequestError: TyroPayRequestError = <TyroPayRequestError>error;
                if (payRequestError.type === TyroPayRequestErrorType.UNKNOWN_ERROR) {
                    throw error;
                }
                return new CardPaymentSubmissionFailure(payRequestError.errorMessage, payRequestError.errorCode, payRequestError.gatewayCode,
                    payRequestError.type === TyroPayRequestErrorType.CLIENT_VALIDATION_ERROR);
            }
            throw error;
        }
    }

    async setupTyroPayForm(): Promise<void> {
        if (this.destroyed || !this.hasBeenUnhidden) {
            return;
        }

        this.enableOrDisableTyroPayForm();

        // Tyro uses the following test to check that it can inject its form - we can't use a ViewChild, because it needs to be in the HTML DOM.
        this.tyroPayFormHolder = document.querySelector("#tyro-card-pay-form-holder");
        if (!this.tyroPayFormHolder) {
            setTimeout(async () => await this.setupTyroPayForm(), 500);
            return;
        }

        // The tyro secret (paymentPlatformPublicKey) changes if the order is re-initiated - the tyro form needs to be rebuilt.
        if (!this.paymentModel.paymentPlatformPublicKey || this.paymentPlatformPublicKey === this.paymentModel.paymentPlatformPublicKey) {
            return;
        }
        this.paymentPlatformPublicKey = this.paymentModel.paymentPlatformPublicKey;

        try {
            console.log("Initialising Tyro for card payment. Live Mode = ", this.paymentModel.paymentPlatformLiveMode);
            this.tyro = (<any>window).Tyro({
                liveMode: this.paymentModel.paymentPlatformLiveMode
            });
            await this.tyro.init(this.paymentPlatformPublicKey);

            const standardPage: ColorPairData = this.themeModel.getPaletteEntry("standard-page");
            const brand: ColorPairData = this.themeModel.getPaletteEntry("brand");
            const danger: ColorPairData = this.themeModel.getPaletteEntry("danger");

            this.payForm = this.tyro.createPayForm({
                options: {
                    creditCardForm: {
                        enabled: true,
                        supportedNetworks: ["visa", "mastercard", "amex"]
                    }
                },
                styleProps: {
                    bodyPadding: "0",
                    showSupportedCards: false,
                    fontFamily: "Inter, sans-serif",
                    labelPosition: "block",
                    labelPadding: "0 0 7px",
                    labelFontSize: "14px",
                    labelFontWeight: "700",
                    labelFontColor: standardPage.foregroundColor,
                    inputBackgroundColor: standardPage.backgroundColor,
                    inputBorderColor: Color.fromString(brand.backgroundColor).applyAlpha(Color.fromString(standardPage.backgroundColor), 0.3).toString(),
                    inputBorderSize: "1px",
                    inputBorderRadius: "3px",
                    inputFontSize: "16px",
                    inputFontWeight: "400",
                    inputFontColor: standardPage.foregroundColor,
                    inputErrorBorderColor: Color.fromString(brand.backgroundColor).applyAlpha(Color.fromString(standardPage.backgroundColor), 0.3).toString(),
                    inputErrorBorderSize: "1px",
                    inputErrorFontColor: standardPage.foregroundColor,
                    inputFocusBorderColor: Color.fromString(brand.backgroundColor).applyAlpha(Color.fromString(standardPage.backgroundColor), 0.5).toString(),
                    inputFocusBorderSize: "1px",
                    inputFocusFontColor: standardPage.foregroundColor,
                    errorFontSize: "14px",
                    errorFontWeight: "600",
                    errorFontColor: danger.backgroundColor
                }
            });
            if ((<HTMLElement>this.tyroPayFormHolder).firstChild) {
                this.tyroPayFormHolder.removeChild((<HTMLElement>this.tyroPayFormHolder).firstChild);
            }
            await this.payForm.inject("#tyro-card-pay-form-holder");
            this.payForm.setReadyListener(() => {
                this.enableOrDisableTyroPayForm();
                this.payFormReady = true;
                this.onPayFormReady.emit(true);
                this.changeDetectorRef.detectChanges();
            });
        } catch (error) {
            console.error("Adyen Tyro pay form error", error);
            this.payFormError = error.message.replace(/\.$/, "");
            setTimeout(() => this.onPayFormReady.emit(false), 300);
            this.changeDetectorRef.detectChanges();
        }
    }

    private enableOrDisableTyroPayForm(): void {
        if (this.tyroPayFormHolder && (<HTMLElement>this.tyroPayFormHolder).firstChild) {
            (<HTMLElement>(<HTMLElement>this.tyroPayFormHolder).firstChild).tabIndex = this.disabled ? -1 : undefined;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/payment/card-payment/tyro-manual-payment-card-panel/tyro-manual-payment-card-panel.component.ts